import React, { useState, useCallback, useEffect } from 'react';
import { 
  // Basic icons
  MapPin, Settings, Construction, Store, FileText, Eye, 
  Trash2, Plus, ChevronDown, Users, Calendar, Upload, X,
  Clock, CheckCircle2, AlertCircle, Loader2, Building2,
  PenTool, Ruler, Brush, Palette, Building, Home,
  LayoutDashboard, FileSearch, Handshake, Share2, Target,
  ChevronRight, Edit2, Save, 
  
  // Additional icons
  Boxes, ShoppingBag, Library, Building as BuildingIcon, 
  Warehouse, School, Coffee, ShoppingCart, Briefcase, 
  Camera, Car, Truck, Plane, Ship, Train, Wifi, 
  Smartphone, Laptop, Desktop, Server, Cloud, Database,
  Key, Lock, Shield, Bell, Flag, Heart, Star,
  Award, Trophy, Medal, Crown, Compass, Check, User,
  FileUp
} from 'lucide-react';

// Available team members data
const availableTeamMembers = [
  { id: 1, name: 'Abdullah', role: 'Real Estate Manager', department: 'Real Estate', status: 'active' },
  { id: 2, name: 'Mishari', role: 'Market Analyst', department: 'Analysis', status: 'active' },
  { id: 3, name: 'Mohammed', role: 'Lead Architect', department: 'Design', status: 'active' },
  { id: 4, name: 'Layla', role: 'Interior Designer', department: 'Design', status: 'active' },
  { id: 5, name: 'Chris', role: 'Project Manager', department: 'Management', status: 'active' },
  { id: 6, name: 'Khairo', role: 'Construction Manager', department: 'Construction', status: 'active' },
  { id: 7, name: 'Mazen', role: 'Senior Engineer', department: 'Engineering', status: 'active' },
  { id: 8, name: 'Ahmed', role: 'Design Coordinator', department: 'Design', status: 'active' }
];

// Phase icons with categories
const phaseIcons = [
  // Buildings & Places
  { icon: MapPin, name: 'Location', category: 'Places' },
  { icon: Building2, name: 'Building', category: 'Places' },
  { icon: Store, name: 'Store', category: 'Places' },
  { icon: Warehouse, name: 'Warehouse', category: 'Places' },
  { icon: BuildingIcon, name: 'Office', category: 'Places' },
  { icon: Library, name: 'Library', category: 'Places' },
  { icon: School, name: 'School', category: 'Places' },
  
  // Construction & Design
  { icon: Construction, name: 'Construction', category: 'Construction' },
  { icon: PenTool, name: 'Design', category: 'Construction' },
  { icon: Ruler, name: 'Measure', category: 'Construction' },
  { icon: Briefcase, name: 'Project', category: 'Construction' },
  
  // Business & Commerce
  { icon: ShoppingBag, name: 'Retail', category: 'Business' },
  { icon: ShoppingCart, name: 'Commerce', category: 'Business' },
  { icon: Boxes, name: 'Inventory', category: 'Business' },
  { icon: Award, name: 'Achievement', category: 'Business' },
  { icon: Trophy, name: 'Success', category: 'Business' },
  
  // Transportation
  { icon: Truck, name: 'Logistics', category: 'Transport' },
  { icon: Car, name: 'Vehicle', category: 'Transport' },
  { icon: Plane, name: 'Air', category: 'Transport' },
  { icon: Ship, name: 'Maritime', category: 'Transport' },
  { icon: Train, name: 'Rail', category: 'Transport' },
  
  // Technology
  { icon: Wifi, name: 'Network', category: 'Tech' },
  { icon: Server, name: 'Infrastructure', category: 'Tech' },
  { icon: Cloud, name: 'Cloud', category: 'Tech' },
  { icon: Database, name: 'Data', category: 'Tech' },
  { icon: Laptop, name: 'System', category: 'Tech' }
];

// Default phases data
const defaultPhases = [
  {
    id: 'site-selection',
    name: 'Site Selection',
    icon: MapPin,
    color: 'indigo',
    progress: 0,
    status: 'not-started',
    description: 'Location analysis and selection process',
    team: [],
    startDate: '',
    endDate: '',
    milestones: []
  },
  {
    id: 'design',
    name: 'Design Development',
    icon: PenTool,
    color: 'blue',
    progress: 0,
    status: 'not-started',
    description: 'Architectural and engineering designs',
    team: [],
    startDate: '',
    endDate: '',
    milestones: []
  },
  {
    id: 'construction',
    name: 'Construction',
    icon: Construction,
    color: 'amber',
    progress: 0,
    status: 'not-started',
    description: 'Building and site development',
    team: [],
    startDate: '',
    endDate: '',
    milestones: []
  },
  {
    id: 'opening',
    name: 'Store Opening',
    icon: Store,
    color: 'emerald',
    progress: 0,
    status: 'not-started',
    description: 'Final preparations and launch',
    team: [],
    startDate: '',
    endDate: '',
    milestones: []
  }
];
// Enhanced Team Member Card Component
const TeamMemberCard = ({ member, isSelected, onClick }) => (
  <button
    onClick={() => onClick(member)}
    className={`
      group cursor-pointer relative overflow-hidden w-full
      ${isSelected ? 'bg-indigo-50/80' : 'hover:bg-gray-50'} 
      rounded-xl border transition-all duration-200
      ${isSelected ? 'border-indigo-200 shadow-sm' : 'border-gray-200'}
    `}
  >
    <div className="p-4 flex items-start gap-4">
      <div className="relative flex-shrink-0">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-50 to-violet-50 
          flex items-center justify-center ring-2 ring-white shadow-sm">
          <span className="text-lg font-semibold text-indigo-600">
            {member.name[0]}
          </span>
        </div>
        {isSelected && (
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-indigo-500 
            rounded-full flex items-center justify-center ring-2 ring-white">
            <CheckCircle2 className="w-3 h-3 text-white" />
          </div>
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-start">
          <div className="min-w-0">
            <h3 className="font-medium text-gray-900 truncate group-hover:text-indigo-600
              transition-colors">{member.name}</h3>
            <p className="text-sm text-gray-500 truncate">{member.role}</p>
          </div>
          <span className={`
            px-2 py-1 text-xs rounded-full flex-shrink-0 ml-2
            ${isSelected ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'}
          `}>
            {member.department}
          </span>
        </div>
        
        <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
          <User className="w-3 h-3" />
          <span>{member.status === 'active' ? 'Active Member' : 'Inactive'}</span>
        </div>
      </div>
    </div>
  </button>
);

// Enhanced File Upload with Progress
const FileUpload = ({ onUpload }) => {
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isDragging, setIsDragging] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(e.type === 'dragenter' || e.type === 'dragover');
  };

  const handleFile = (file) => {
    const fileInfo = {
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      size: `${(file.size / (1024 * 1024)).toFixed(2)} MB`,
      type: file.type,
      uploadDate: new Date().toLocaleDateString()
    };

    setFiles(prev => [...prev, fileInfo]);

    // Simulate upload progress
    let progress = 0;
    const interval = setInterval(() => {
      progress += 5;
      setUploadProgress(prev => ({
        ...prev,
        [fileInfo.id]: progress
      }));

      if (progress >= 100) {
        clearInterval(interval);
        onUpload(fileInfo);
        setTimeout(() => {
          setFiles(prev => prev.filter(f => f.id !== fileInfo.id));
          setUploadProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[fileInfo.id];
            return newProgress;
          });
        }, 1000);
      }
    }, 100);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  return (
    <div className="space-y-4">
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`
          relative rounded-xl border-2 border-dashed p-6
          transition-all duration-200 group
          ${isDragging
            ? 'border-indigo-400 bg-indigo-50'
            : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50'
          }
        `}
      >
        <input
          type="file"
          onChange={(e) => e.target.files[0] && handleFile(e.target.files[0])}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
        <div className="text-center">
          <FileUp className={`
            w-10 h-10 mx-auto mb-3 transition-colors duration-200
            ${isDragging ? 'text-indigo-500' : 'text-gray-400 group-hover:text-indigo-500'}
          `} />
          <p className="text-sm font-medium text-gray-700">
            {isDragging ? "Drop your file here" : "Click to upload or drag and drop"}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            Maximum file size: 50MB
          </p>
        </div>
      </div>

      {/* File upload progress */}
      {files.map(file => (
        <div
          key={file.id}
          className="flex items-center gap-4 p-4 bg-white rounded-xl border
            border-gray-200 shadow-sm hover:border-gray-300 transition-all"
        >
          <FileText className="w-5 h-5 text-indigo-500" />
          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-center mb-1">
              <p className="text-sm font-medium text-gray-700 truncate">
                {file.name}
              </p>
              <span className="text-xs text-gray-500">{file.size}</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
              <div 
                className="h-full bg-indigo-500 transition-all duration-200"
                style={{ width: `${uploadProgress[file.id] || 0}%` }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
// Status Badge Component
const StatusBadge = ({ status, onClick, showDropdown }) => {
  const statusConfig = {
    'not-started': {
      icon: Clock,
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-700',
      borderColor: 'border-gray-200',
      iconColor: 'text-gray-500',
      label: 'Not Started'
    },
    'in-progress': {
      icon: Loader2,
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700',
      borderColor: 'border-blue-200',
      iconColor: 'text-blue-500',
      label: 'In Progress'
    },
    'completed': {
      icon: CheckCircle2,
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-700',
      borderColor: 'border-emerald-200',
      iconColor: 'text-emerald-500',
      label: 'Completed'
    },
    'delayed': {
      icon: AlertCircle,
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-700',
      borderColor: 'border-amber-200',
      iconColor: 'text-amber-500',
      label: 'Delayed'
    }
  };

  const config = statusConfig[status] || statusConfig['not-started'];
  const Icon = config.icon;

  return (
    <button
      onClick={onClick}
      className={`
        inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full 
        border ${config.borderColor} ${config.bgColor} ${config.textColor}
        text-sm font-medium transition-all duration-200
        hover:shadow-sm ${showDropdown ? 'scale-105' : 'hover:scale-105'}
        ${onClick ? 'cursor-pointer' : 'cursor-default'}
      `}
    >
      <Icon className={`w-4 h-4 ${config.iconColor} ${status === 'in-progress' ? 'animate-spin' : ''}`} />
      {config.label}
      {onClick && <ChevronDown className="w-4 h-4 ml-1" />}
    </button>
  );
};

// Enhanced Progress Bar Component
const ProgressBar = ({ progress, variant = 'default', size = 'default' }) => {
  const variants = {
    default: 'from-blue-500 to-indigo-500',
    success: 'from-emerald-500 to-green-500',
    warning: 'from-amber-500 to-yellow-500',
  };

  const sizes = {
    small: 'h-1.5',
    default: 'h-2.5',
    large: 'h-3'
  };

  return (
    <div className={`w-full bg-gray-100 rounded-full overflow-hidden ${sizes[size]}`}>
      <div 
        className={`h-full rounded-full bg-gradient-to-r ${variants[variant]} 
          transition-all duration-700 ease-out relative`}
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

// Modal Component
const Modal = ({ show, onClose, title, children }) => {
  if (!show) return null;
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div 
        className="fixed inset-0 bg-black/30 backdrop-blur-sm transition-opacity duration-300" 
        onClick={onClose}
      />
      <div className="flex items-center justify-center min-h-screen p-4">
        <div 
          className="relative bg-white w-full max-w-2xl rounded-xl shadow-lg
            transform transition-all duration-300 scale-100 opacity-100
            border border-gray-200"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900">
              {title}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 p-2 rounded-lg
                hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

// Status Dropdown Component
const StatusDropdown = ({ status, onStatusChange }) => {
  const [isOpen, setIsOpen] = useState(false);

  const statuses = [
    { value: 'not-started', label: 'Not Started' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'delayed', label: 'Delayed' }
  ];

  return (
    <div className="relative">
      <StatusBadge 
        status={status} 
        onClick={() => setIsOpen(!isOpen)}
        showDropdown={isOpen}
      />
      
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-48 rounded-lg shadow-lg 
            bg-white ring-1 ring-black ring-opacity-5 z-20">
            <div className="py-1">
              {statuses.map((s) => (
                <button
                  key={s.value}
                  onClick={() => {
                    onStatusChange(s.value);
                    setIsOpen(false);
                  }}
                  className={`
                    w-full px-4 py-2 text-sm text-left
                    ${status === s.value ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'}
                    hover:bg-gray-50
                  `}
                >
                  <span className="flex items-center">
                    <StatusBadge status={s.value} />
                  </span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Icon Selector Component
const IconSelector = ({ selectedIcon, onSelect }) => {
  const categories = [...new Set(phaseIcons.map(icon => icon.category))];
  
  return (
    <div className="space-y-6">
      {categories.map(category => (
        <div key={category}>
          <h4 className="text-sm font-medium text-gray-700 mb-3">{category}</h4>
          <div className="grid grid-cols-4 gap-3">
            {phaseIcons
              .filter(icon => icon.category === category)
              .map(({ icon: Icon, name }) => (
                <button
                  key={name}
                  onClick={() => onSelect(Icon)}
                  className={`
                    p-4 rounded-xl border transition-all duration-200
                    flex flex-col items-center gap-2 hover:scale-105
                    ${Icon === selectedIcon
                      ? 'bg-indigo-50 border-indigo-200 text-indigo-600 shadow-sm'
                      : 'border-gray-200 hover:border-indigo-200 hover:bg-gray-50'
                    }
                  `}
                >
                  <Icon className="w-6 h-6" />
                  <span className="text-xs font-medium truncate w-full text-center">{name}</span>
                </button>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
};
// Enhanced Phase Card Component
const PhaseCard = ({ phase, isActive, onClick }) => {
  const Icon = phase.icon;
  
  return (
    <div
      onClick={onClick}
      className={`
        relative w-full cursor-pointer
        ${isActive 
          ? 'bg-gradient-to-br from-indigo-50/50 to-violet-50/50'
          : 'hover:bg-gray-50/80'
        }
        rounded-xl transition-all duration-200 group
      `}
    >
      {isActive && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8
          bg-indigo-600 rounded-r-full" />
      )}

      <div className="p-4 pl-6">
        <div className="flex items-start gap-4">
          <div className={`
            relative p-3 rounded-xl transition-all duration-200 flex-shrink-0
            ${isActive
              ? 'bg-white shadow-md text-indigo-600 ring-1 ring-indigo-100'
              : 'bg-gray-100/80 text-gray-600 group-hover:text-gray-800 group-hover:bg-gray-100'
            }
          `}>
            <Icon className="w-6 h-6" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className={`
                  font-medium truncate mb-1
                  ${isActive ? 'text-indigo-900' : 'text-gray-900'}
                `}>
                  {phase.name}
                </h3>
                <StatusBadge status={phase.status} />
              </div>
            </div>

            <div className="mt-4 space-y-2">
              <ProgressBar 
                progress={phase.progress}
                variant={phase.status === 'completed' ? 'success' : 'default'}
                size="small"
              />
              
              <div className="flex items-center justify-between text-sm">
                <span className={`
                  font-medium
                  ${phase.status === 'completed' ? 'text-emerald-600' : 'text-gray-600'}
                `}>
                  {phase.progress}% Complete
                </span>
                
                <div className="flex -space-x-2">
                  {phase.team.slice(0, 3).map((member, index) => (
                    <div 
                      key={member.id}
                      className="relative"
                      style={{ zIndex: phase.team.length - index }}
                    >
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br 
                        from-indigo-100 to-violet-100 flex items-center justify-center
                        ring-2 ring-white text-xs font-medium text-indigo-600">
                        {member.name[0]}
                      </div>
                    </div>
                  ))}
                  {phase.team.length > 3 && (
                    <div className="relative" style={{ zIndex: 0 }}>
                      <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center 
                        justify-center ring-2 ring-white text-xs font-medium text-gray-600">
                        +{phase.team.length - 3}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {phase.startDate && phase.endDate && (
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Calendar className="w-3 h-3" />
                  <span>{phase.startDate}</span>
                  <ChevronRight className="w-3 h-3" />
                  <span>{phase.endDate}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main PhasesTab Component
const PhasesTab = () => {
  const [phases, setPhases] = useState(defaultPhases);
  const [activePhase, setActivePhase] = useState(phases[0]?.id);
  const [showAddPhase, setShowAddPhase] = useState(false);
  const [showAddMilestone, setShowAddMilestone] = useState(false);
  const [expandedMilestones, setExpandedMilestones] = useState(new Set());
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState('All');
  const [uploadedFiles, setUploadedFiles] = useState({});

  const [newPhase, setNewPhase] = useState({
    name: '',
    description: '',
    icon: MapPin,
    startDate: '',
    endDate: '',
    team: []
  });

  const [newMilestone, setNewMilestone] = useState({
    name: '',
    description: '',
    status: 'not-started',
    deadline: '',
    files: []
  });

  // Get unique departments for filter
  const departments = ['All', ...new Set(availableTeamMembers.map(member => member.department))];

  // Filter team members by department
  const filteredTeamMembers = selectedDepartment === 'All'
    ? availableTeamMembers
    : availableTeamMembers.filter(member => member.department === selectedDepartment);

  const handleAddTeamMember = useCallback((member) => {
    setNewPhase(prev => ({
      ...prev,
      team: prev.team.some(m => m.id === member.id)
        ? prev.team.filter(m => m.id !== member.id)
        : [...prev.team, member]
    }));
  }, []);

  const toggleMilestone = useCallback((milestoneId) => {
    setExpandedMilestones(prev => {
      const newSet = new Set(prev);
      if (newSet.has(milestoneId)) {
        newSet.delete(milestoneId);
      } else {
        newSet.add(milestoneId);
      }
      return newSet;
    });
  }, []);

  const handleMilestoneStatusChange = useCallback((milestoneId, newStatus) => {
    setPhases(prev => prev.map(phase => {
      if (phase.id === activePhase) {
        const updatedMilestones = phase.milestones.map(m => 
          m.id === milestoneId ? { ...m, status: newStatus } : m
        );
        
        // Recalculate phase progress based on milestones
        const completedCount = updatedMilestones.filter(m => m.status === 'completed').length;
        const inProgressCount = updatedMilestones.filter(m => m.status === 'in-progress').length;
        const totalMilestones = updatedMilestones.length;
        const progress = totalMilestones > 0 
          ? Math.round(((completedCount + (inProgressCount * 0.5)) / totalMilestones) * 100)
          : 0;

        return {
          ...phase,
          milestones: updatedMilestones,
          progress
        };
      }
      return phase;
    }));
  }, [activePhase]);

  const handleFileUpload = useCallback((milestoneId, file) => {
    setUploadedFiles(prev => ({
      ...prev,
      [milestoneId]: [...(prev[milestoneId] || []), file]
    }));
  }, []);

  const handleAddMilestone = useCallback(() => {
    if (!newMilestone.name) return;

    const milestone = {
      id: `milestone-${Date.now()}`,
      ...newMilestone,
      files: []
    };

    setPhases(prev => prev.map(phase => {
      if (phase.id === activePhase) {
        return {
          ...phase,
          milestones: [...phase.milestones, milestone]
        };
      }
      return phase;
    }));

    setShowAddMilestone(false);
    setNewMilestone({
      name: '',
      description: '',
      status: 'not-started',
      deadline: '',
      files: []
    });
  }, [activePhase, newMilestone]);

  const handleEditMilestone = useCallback(() => {
    if (!newMilestone.name || !editingMilestone) return;

    setPhases(prev => prev.map(phase => {
      if (phase.id === activePhase) {
        return {
          ...phase,
          milestones: phase.milestones.map(m => 
            m.id === editingMilestone.id ? { ...m, ...newMilestone } : m
          )
        };
      }
      return phase;
    }));

    setShowAddMilestone(false);
    setEditingMilestone(null);
    setNewMilestone({
      name: '',
      description: '',
      status: 'not-started',
      deadline: '',
      files: []
    });
  }, [activePhase, editingMilestone, newMilestone]);

  const handlePhaseCompletion = useCallback(() => {
    setPhases(prev => prev.map(phase => {
      if (phase.id === activePhase) {
        return {
          ...phase,
          status: 'completed',
          progress: 100,
          milestones: phase.milestones.map(milestone => ({
            ...milestone,
            status: 'completed'
          }))
        };
      }
      return phase;
    }));
  }, [activePhase]);

  // First, add mobile detection
  const [isMobile, setIsMobile] = useState(false);
  const [expandedPhase, setExpandedPhase] = useState(null);

  // Add mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Add this new function for mobile phase expansion
  const handleMobilePhaseClick = (phaseId) => {
    if (isMobile) {
      setExpandedPhase(expandedPhase === phaseId ? null : phaseId);
    }
  };

  // Modify only the return statement to handle mobile view
  if (isMobile) {
    return (
      <div className="h-full bg-gray-50 relative pb-24">
        <div className="p-4 space-y-3">
          {phases.map((phase) => (
            <div 
              key={phase.id} 
              className="bg-white rounded-xl border border-gray-200 overflow-hidden"
            >
              <button
                onClick={() => {
                  handleMobilePhaseClick(phase.id);
                  setActivePhase(phase.id);
                }}
                className="w-full p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                    <phase.icon className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-medium text-gray-900">{phase.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <StatusBadge status={phase.status} />
                      <span className="text-sm text-gray-500">
                        {phase.progress}% Complete
                      </span>
                    </div>
                  </div>
                </div>
                <ChevronRight 
                  className={`w-5 h-5 text-gray-400 transition-transform duration-200
                    ${expandedPhase === phase.id ? 'rotate-90' : ''}`}
                />
              </button>

              {expandedPhase === phase.id && (
                <div className="border-t border-gray-100">
                  {/* Description */}
                  <div className="p-4 border-b border-gray-100">
                    <p className="text-sm text-gray-600">{phase.description}</p>
                  </div>

                  {/* Team */}
                  <div className="p-4 border-b border-gray-100">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Team Members</h4>
                    <div className="flex flex-wrap gap-2">
                      {phase.team.map((member) => (
                        <div 
                          key={member.id}
                          className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-full"
                        >
                          <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center">
                            <span className="text-xs font-medium text-indigo-600">
                              {member.name[0]}
                            </span>
                          </div>
                          <span className="text-sm text-gray-700">{member.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Milestones */}
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-sm font-medium text-gray-900">Milestones</h4>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setShowAddMilestone(true);
                        }}
                        className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-2">
                      {phase.milestones.map((milestone) => (
                        <div
                          key={milestone.id}
                          className="bg-gray-50 rounded-lg p-3"
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <h5 className="font-medium text-gray-900">{milestone.name}</h5>
                              {milestone.deadline && (
                                <p className="text-xs text-gray-500 mt-1 flex items-center">
                                  <Calendar className="w-3 h-3 mr-1" />
                                  {milestone.deadline}
                                </p>
                              )}
                            </div>
                            <StatusBadge status={milestone.status} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Move Complete Phase Button here, before Files section */}
                  {phase.status !== 'completed' && (
                    <div className="p-4 border-t border-gray-100">
                      <button
                        onClick={handlePhaseCompletion}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3
                          bg-emerald-600 text-white rounded-xl font-medium"
                      >
                        <Check className="w-5 h-5" />
                        Complete Phase
                      </button>
                    </div>
                  )}

                  {/* Files */}
                  <div className="p-4 border-t border-gray-100">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Files</h4>
                    <FileUpload onUpload={(file) => handleFileUpload(phase.id, file)} />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Fixed Add Phase Button - Adjusted for bottom menu */}
        <div className="fixed bottom-[64px] left-0 right-0 z-40">
          <div className="p-4 bg-white border-t border-gray-200 shadow-lg">
            <button
              onClick={() => setShowAddPhase(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3
                bg-indigo-600 text-white rounded-xl font-medium"
            >
              <Plus className="w-5 h-5" />
              Add New Phase
            </button>
          </div>
        </div>

        {/* Modals */}
        <Modal
          show={showAddPhase}
          onClose={() => setShowAddPhase(false)}
          title="Add New Phase"
        >
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Phase Icon
              </label>
              <IconSelector
                selectedIcon={newPhase.icon}
                onSelect={(icon) => setNewPhase(prev => ({ ...prev, icon }))}
              />
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phase Name *
                </label>
                <input
                  type="text"
                  value={newPhase.name}
                  onChange={(e) => setNewPhase(prev => ({ 
                    ...prev, 
                    name: e.target.value 
                  }))}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 
                    focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
                  placeholder="Enter phase name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={newPhase.description}
                  onChange={(e) => setNewPhase(prev => ({ 
                    ...prev, 
                    description: e.target.value 
                  }))}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 
                    focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
                  placeholder="Enter phase description"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={newPhase.startDate}
                    onChange={(e) => setNewPhase(prev => ({ 
                      ...prev, 
                      startDate: e.target.value 
                    }))}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 
                      focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date *
                  </label>
                  <input
                    type="date"
                    value={newPhase.endDate}
                    onChange={(e) => setNewPhase(prev => ({ 
                      ...prev, 
                      endDate: e.target.value 
                    }))}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 
                      focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Team Members
                  </label>
                  <select
                    value={selectedDepartment}
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                    className="text-sm border border-gray-300 rounded-lg px-3 py-1.5
                      focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
                  >
                    {departments.map(dept => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-1 gap-4 max-h-[300px] overflow-y-auto pr-2">
                  {filteredTeamMembers.map(member => (
                    <TeamMemberCard
                      key={member.id}
                      member={member}
                      isSelected={newPhase.team.some(m => m.id === member.id)}
                      onClick={handleAddTeamMember}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-4">
              <button
                onClick={() => setShowAddPhase(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 
                  bg-white border border-gray-300 rounded-lg hover:bg-gray-50
                  transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!newPhase.name || !newPhase.startDate || !newPhase.endDate) return;
                  setPhases(prev => [...prev, {
                    ...newPhase,
                    id: `phase-${Date.now()}`,
                    status: 'not-started',
                    progress: 0,
                    milestones: []
                  }]);
                  setShowAddPhase(false);
                  setNewPhase({
                    name: '',
                    description: '',
                    icon: MapPin,
                    startDate: '',
                    endDate: '',
                    team: []
                  });
                }}
                className="px-4 py-2 text-sm font-medium text-white
                  bg-indigo-600 rounded-lg hover:bg-indigo-700
                  transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!newPhase.name || !newPhase.startDate || !newPhase.endDate}
              >
                Create Phase
              </button>
            </div>
          </div>
        </Modal>

        <Modal
          show={showAddMilestone}
          onClose={() => {
            setShowAddMilestone(false);
            setEditingMilestone(null);
            setNewMilestone({
              name: '',
              description: '',
              status: 'not-started',
              deadline: ''
            });
          }}
          title={editingMilestone ? "Edit Milestone" : "Add New Milestone"}
        >
          <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Milestone Name *
                </label>
                <input
                  type="text"
                  value={newMilestone.name}
                  onChange={(e) => setNewMilestone(prev => ({ 
                    ...prev, 
                    name: e.target.value 
                  }))}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 
                    focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
                  placeholder="Enter milestone name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={newMilestone.description}
                  onChange={(e) => setNewMilestone(prev => ({ 
                    ...prev, 
                    description: e.target.value 
                  }))}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 
                    focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500
                    resize-none"
                  placeholder="Describe the milestone objectives and requirements"
                  rows={4}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deadline
                </label>
                <input
                  type="date"
                  value={newMilestone.deadline}
                  onChange={(e) => setNewMilestone(prev => ({ 
                    ...prev, 
                    deadline: e.target.value 
                  }))}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 
                    focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={newMilestone.status}
                  onChange={(e) => setNewMilestone(prev => ({ 
                    ...prev, 
                    status: e.target.value 
                  }))}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 
                    focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
                >
                  <option value="not-started">Not Started</option>
                  <option value="in-progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="delayed">Delayed</option>
                </select>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowAddMilestone(false);
                    setEditingMilestone(null);
                    setNewMilestone({
                      name: '',
                      description: '',
                      status: 'not-started',
                      deadline: ''
                    });
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 
                    bg-white border border-gray-300 rounded-lg hover:bg-gray-50
                    transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={editingMilestone ? handleEditMilestone : handleAddMilestone}
                  className="px-4 py-2 text-sm font-medium text-white
                    bg-indigo-600 rounded-lg hover:bg-indigo-700
                    transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!newMilestone.name}
                >
                  {editingMilestone ? 'Update Milestone' : 'Create Milestone'}
                </button>
              </div>
            </div>
          </Modal>
      </div>
    );
  }

  // Return your existing desktop layout completely unchanged
  return (
    <div className="flex h-full bg-gray-50/50">
      {/* Left sidebar - Phase Navigation */}
      <div className="w-[360px] border-r border-gray-200/50 bg-white/80 backdrop-blur-sm
        flex flex-col">
        <div className="flex-1 p-4">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Project Phases</h2>
            <button
              onClick={() => setShowAddPhase(true)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 
                rounded-lg transition-colors"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-3">
            {phases.map(phase => (
              <PhaseCard
                key={phase.id}
                phase={phase}
                isActive={activePhase === phase.id}
                onClick={() => setActivePhase(phase.id)}
              />
            ))}
          </div>
        </div>

        {/* Original Add Phase Button position */}
        <div className="p-4 border-t border-gray-200/50">
          <button
            onClick={() => setShowAddPhase(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5
              bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-100
              transition-colors font-medium"
          >
            <Plus className="w-5 h-5" />
            Add New Phase
          </button>
        </div>
      </div>

      {/* Main content area */}
      {activePhase && (
        <div className="flex-1 overflow-auto">
          <div className="h-full">
            {/* Phase header */}
            <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-sm border-b border-gray-200/50">
              <div className="px-6 py-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h1 className="text-2xl font-semibold text-gray-900">
                      {phases.find(p => p.id === activePhase)?.name}
                    </h1>
                    <p className="mt-1 text-gray-500">
                      {phases.find(p => p.id === activePhase)?.description}
                    </p>

                    {/* Team members */}
                    <div className="mt-4 flex items-center gap-2">
                      <h3 className="text-sm font-medium text-gray-700">Team</h3>
                      <div className="flex -space-x-2">
                        {phases.find(p => p.id === activePhase)?.team.map(member => (
                          <div 
                            key={member.id}
                            className="relative"
                            title={`${member.name} - ${member.role}`}
                          >
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-100 to-violet-100 
                              flex items-center justify-center ring-2 ring-white">
                              <span className="text-sm font-semibold text-indigo-600">
                                {member.name[0]}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <StatusDropdown
                      status={phases.find(p => p.id === activePhase)?.status}
                      onStatusChange={(newStatus) => {
                        setPhases(prev => prev.map(phase => 
                          phase.id === activePhase 
                            ? { ...phase, status: newStatus }
                            : phase
                        ));
                      }}
                    />
                    <button
                      onClick={() => setShowAddMilestone(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-indigo-600 
                        text-white rounded-lg hover:bg-indigo-700 font-medium
                        transition-colors shadow-sm"
                    >
                      <Plus className="w-4 h-4" />
                      Add Milestone
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Milestones */}
            <div className="p-6">
              <div className="space-y-4">
                {phases.find(p => p.id === activePhase)?.milestones.map(milestone => (
                  <div 
                    key={milestone.id}
                    className="bg-white rounded-xl border border-gray-200 shadow-sm
                      hover:border-gray-300 transition-all duration-200"
                  >
                    <div className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => toggleMilestone(milestone.id)}
                            className={`
                              p-2 rounded-lg transition-colors
                              ${expandedMilestones.has(milestone.id)
                                ? 'bg-gray-100 text-gray-700'
                                : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'
                              }
                            `}
                          >
                            <ChevronRight 
                              className={`
                                w-5 h-5 transition-transform duration-200
                                ${expandedMilestones.has(milestone.id) ? 'transform rotate-90' : ''}
                              `}
                            />
                          </button>
                          <div>
                            <h3 className="font-medium text-gray-900">{milestone.name}</h3>
                            {milestone.description && (
                              <p className="text-sm text-gray-500 mt-1">
                                {milestone.description}
                              </p>
                            )}
                            {milestone.deadline && (
                              <p className="text-sm text-gray-500 mt-1 flex items-center">
                                <Calendar className="w-4 h-4 mr-1" />
                                Due: {milestone.deadline}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <StatusDropdown
                            status={milestone.status}
                            onStatusChange={(newStatus) => handleMilestoneStatusChange(milestone.id, newStatus)}
                          />
                          <button
                            onClick={() => {
                              setEditingMilestone(milestone);
                              setNewMilestone(milestone);
                              setShowAddMilestone(true);
                            }}
                            className="p-2 text-gray-400 hover:text-gray-700 
                              hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {expandedMilestones.has(milestone.id) && (
                        <div className="mt-4 pl-8 space-y-4">
                          <FileUpload 
                            onUpload={(file) => handleFileUpload(milestone.id, file)}
                          />
                          
                          {/* Display uploaded files */}
                          {uploadedFiles[milestone.id]?.length > 0 && (
                            <div className="space-y-2">
                              {uploadedFiles[milestone.id].map((file, index) => (
                                <div 
                                  key={index}
                                  className="flex items-center justify-between p-3 
                                    bg-gray-50 rounded-lg"
                                >
                                  <div className="flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-gray-500" />
                                    <span className="text-sm text-gray-700">{file.name}</span>
                                  </div>
                                  <span className="text-xs text-gray-500">{file.size}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Phase Modal */}
      <Modal
        show={showAddPhase}
        onClose={() => setShowAddPhase(false)}
        title="Add New Phase"
      >
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Phase Icon
            </label>
            <IconSelector
              selectedIcon={newPhase.icon}
              onSelect={(icon) => setNewPhase(prev => ({ ...prev, icon }))}
            />
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phase Name *
              </label>
              <input
                type="text"
                value={newPhase.name}
                onChange={(e) => setNewPhase(prev => ({ 
                  ...prev, 
                  name: e.target.value 
                }))}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 
                  focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
                placeholder="Enter phase name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={newPhase.description}
                onChange={(e) => setNewPhase(prev => ({ 
                  ...prev, 
                  description: e.target.value 
                }))}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 
                  focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
                placeholder="Enter phase description"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date *
                </label>
                <input
                  type="date"
                  value={newPhase.startDate}
                  onChange={(e) => setNewPhase(prev => ({ 
                    ...prev, 
                    startDate: e.target.value 
                  }))}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 
                    focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date *
                </label>
                <input
                  type="date"
                  value={newPhase.endDate}
                  onChange={(e) => setNewPhase(prev => ({ 
                    ...prev, 
                    endDate: e.target.value 
                  }))}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 
                    focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  Team Members
                </label>
                <select
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                  className="text-sm border border-gray-300 rounded-lg px-3 py-1.5
                    focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
                >
                  {departments.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 gap-4 max-h-[300px] overflow-y-auto pr-2">
                {filteredTeamMembers.map(member => (
                  <TeamMemberCard
                    key={member.id}
                    member={member}
                    isSelected={newPhase.team.some(m => m.id === member.id)}
                    onClick={handleAddTeamMember}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4">
            <button
              onClick={() => setShowAddPhase(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 
                bg-white border border-gray-300 rounded-lg hover:bg-gray-50
                transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (!newPhase.name || !newPhase.startDate || !newPhase.endDate) return;
                setPhases(prev => [...prev, {
                  ...newPhase,
                  id: `phase-${Date.now()}`,
                  status: 'not-started',
                  progress: 0,
                  milestones: []
                }]);
                setShowAddPhase(false);
                setNewPhase({
                  name: '',
                  description: '',
                  icon: MapPin,
                  startDate: '',
                  endDate: '',
                  team: []
                });
              }}
              className="px-4 py-2 text-sm font-medium text-white
                bg-indigo-600 rounded-lg hover:bg-indigo-700
                transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!newPhase.name || !newPhase.startDate || !newPhase.endDate}
            >
              Create Phase
            </button>
          </div>
        </div>
      </Modal>

      {/* Add/Edit Milestone Modal */}
      <Modal
        show={showAddMilestone}
        onClose={() => {
          setShowAddMilestone(false);
          setEditingMilestone(null);
          setNewMilestone({
            name: '',
            description: '',
            status: 'not-started',
            deadline: ''
          });
        }}
        title={editingMilestone ? "Edit Milestone" : "Add New Milestone"}
      >
        <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Milestone Name *
              </label>
              <input
                type="text"
                value={newMilestone.name}
                onChange={(e) => setNewMilestone(prev => ({ 
                  ...prev, 
                  name: e.target.value 
                }))}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 
                  focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
                placeholder="Enter milestone name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={newMilestone.description}
                onChange={(e) => setNewMilestone(prev => ({ 
                  ...prev, 
                  description: e.target.value 
                }))}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 
                  focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500
                  resize-none"
                placeholder="Describe the milestone objectives and requirements"
                rows={4}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Deadline
              </label>
              <input
                type="date"
                value={newMilestone.deadline}
                onChange={(e) => setNewMilestone(prev => ({ 
                  ...prev, 
                  deadline: e.target.value 
                }))}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 
                  focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={newMilestone.status}
                onChange={(e) => setNewMilestone(prev => ({ 
                  ...prev, 
                  status: e.target.value 
                }))}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 
                  focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500"
              >
                <option value="not-started">Not Started</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="delayed">Delayed</option>
              </select>
            </div>

            <div className="flex items-center justify-end gap-3 pt-4">
              <button
                onClick={() => {
                  setShowAddMilestone(false);
                  setEditingMilestone(null);
                  setNewMilestone({
                    name: '',
                    description: '',
                    status: 'not-started',
                    deadline: ''
                  });
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 
                  bg-white border border-gray-300 rounded-lg hover:bg-gray-50
                  transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={editingMilestone ? handleEditMilestone : handleAddMilestone}
                className="px-4 py-2 text-sm font-medium text-white
                  bg-indigo-600 rounded-lg hover:bg-indigo-700
                  transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!newMilestone.name}
              >
                {editingMilestone ? 'Update Milestone' : 'Create Milestone'}
              </button>
            </div>
          </div>
        </Modal>

        {/* Complete Phase Button */}
        {activePhase && phases.find(p => p.id === activePhase)?.status !== 'completed' && (
          <div className="fixed bottom-6 right-6">
            <button
              onClick={handlePhaseCompletion}
              className="flex items-center gap-2 px-6 py-3 bg-emerald-600 
                text-white rounded-xl hover:bg-emerald-700 font-medium
                transition-colors shadow-lg hover:shadow-xl"
            >
              <Check className="w-5 h-5" />
              Complete Phase
            </button>
          </div>
        )}
      </div>
    );
  };

export default PhasesTab;
